import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, TrendingUp, Shield, Zap, LineChart, Brain, Lock, BarChart3, Cpu, Database } from 'lucide-react';
import { companyInfo, heroContent, aboutContent } from '../data/mock';
import HFTTerminal from '../components/HFTTerminal';
import CinematicHeroBackground from '../components/CinematicHeroBackground';

// Static container animation variants for stagger
const CONTAINER_VARIANTS = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.15
    }
  }
};

const ITEM_VARIANTS = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      damping: 12,
      stiffness: 100
    }
  }
};

const FEATURES_DATA = [
  {
    icon: <BarChart3 size={48} />,
    title: "Quantitative Research",
    description: "Proprietary mathematical models, statistical research, and systematic strategy development.",
    gradient: "linear-gradient(135deg, #00ffd1 0%, #00b395 100%)"
  },
  {
    icon: <Shield size={48} />,
    title: "Compliance First",
    description: "Rigorous adherence to SEBI, MCA, RBI regulations and applicable Indian regulatory frameworks.",
    gradient: "linear-gradient(135deg, #00ffd1 0%, #00b395 100%)"
  },
  {
    icon: <Cpu size={48} />,
    title: "Technology Infrastructure",
    description: "AI, machine learning, market data engineering, automation, and execution infrastructure.",
    gradient: "linear-gradient(135deg, #00ffd1 0%, #00b395 100%)"
  }
];

const TRUST_BADGES = [
  "MCA Registered",
  "Companies Act Compliant",
  "FEMA Compliance Framework",
  "Governance & Compliance First"
];

const Home = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    // Respect reduced-motion preference & avoid autoplay on slow connections
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const saveData = connection?.saveData;
    if (prefersReducedMotion || saveData) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
  }, []);

  return (
    <div className="page-container">
      {/* Hero Section with Interactive Full-Screen Canvas Background */}
      <section className="hero-section-enhanced">
        {/* Full-Screen Animated Math & Quant Background Canvas */}
        <div className="hero-video-bg">
          <CinematicHeroBackground />
          <div className="hero-video-vignette"></div>
          <div className="gradient-overlay"></div>
          <div className="animated-grid"></div>
        </div>

        <motion.div 
          className="hero-content"
          style={{ y, opacity }}
          variants={CONTAINER_VARIANTS}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="hero-badge" variants={ITEM_VARIANTS}>
            <span className="badge-dot"></span>
            Quantitative Research & Systematic Trading • Est. 2025
          </motion.div>

          <motion.h1 
            className="display-huge hero-title"
            variants={ITEM_VARIANTS}
          >
            Engineering Market Intelligence Through Quantitative Research, Algorithms, and Disciplined Capital Deployment.
          </motion.h1>
          
          <motion.p 
            className="body-large hero-subheadline"
            variants={ITEM_VARIANTS}
            style={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.75' }}
          >
            A technology-driven proprietary trading and quantitative research firm deploying our own capital to trade and invest in Indian securities, derivatives, commodities, and digital assets as permitted by law.
          </motion.p>
          
          <motion.div 
            className="hero-cta-group"
            variants={ITEM_VARIANTS}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/careers" className="btn-primary btn-glow">
                Join Our Team
                <ArrowRight size={20} />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/about" className="btn-secondary">
                About Us
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </motion.div>

          {/* Animated Stats Bar */}
          <motion.div 
            className="hero-stats-bar"
            variants={ITEM_VARIANTS}
          >
            <div className="stat-item">
              <span className="stat-number">100%</span>
              <span className="stat-label">Proprietary Capital</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">Institutional</span>
              <span className="stat-label">Quantitative Research</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">AI & ML</span>
              <span className="stat-label">Driven Execution</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">Compliance-First</span>
              <span className="stat-label">Operations</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator removed per user request */}
      </section>

      {/* Features Section with Images */}
      <section className="features-section-enhanced">
        <div className="features-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="display-medium">Why Quanentry Capital?</h2>
            <p className="body-large section-subtitle" style={{ color: 'rgba(255, 255, 255, 0.82)', lineHeight: '1.75' }}>
              Quanentry Capital Private Limited develops proprietary quantitative research, mathematical models, and advanced technology to generate data-driven market intelligence and systematically deploy its own capital across regulated financial markets.
            </p>
          </motion.div>
          
          <div className="features-grid-enhanced">
            {FEATURES_DATA.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card-enhanced"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                whileHover={{ 
                  y: -12,
                  transition: { duration: 0.3 }
                }}
              >
                <div className="feature-icon-wrapper" style={{ background: feature.gradient }}>
                  {feature.icon}
                </div>
                <h3 className="heading-2">{feature.title}</h3>
                <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.80)', lineHeight: '1.6' }}>{feature.description}</p>
                <div className="feature-shine"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Technology Showcase */}
      <section className="tech-showcase-section">
        <div className="tech-showcase-content">
          <div className="tech-showcase-grid">
            <motion.div 
              className="tech-showcase-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="display-medium">Powered by Advanced Technology</h2>
              <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.82)', lineHeight: '1.75' }}>
                Our quantitative trading infrastructure integrates predictive analytics, real-time market data engineering, and enterprise-grade security to ensure disciplined systematic execution.
              </p>
              
              <div className="tech-features-list">
                <div className="tech-feature-item">
                  <Brain size={24} />
                  <div>
                    <h4 className="heading-3">Machine Learning & Artificial Intelligence</h4>
                    <p className="body-small" style={{ color: 'rgba(255, 255, 255, 0.78)' }}>Advanced predictive models and computational intelligence</p>
                  </div>
                </div>
                <div className="tech-feature-item">
                  <Database size={24} />
                  <div>
                    <h4 className="heading-3">Real-Time Market Data Infrastructure</h4>
                    <p className="body-small" style={{ color: 'rgba(255, 255, 255, 0.78)' }}>Low-latency data processing and systematic routing</p>
                  </div>
                </div>
                <div className="tech-feature-item">
                  <Lock size={24} />
                  <div>
                    <h4 className="heading-3">Enterprise Security & Governance</h4>
                    <p className="body-small" style={{ color: 'rgba(255, 255, 255, 0.78)' }}>Enterprise-grade security architecture</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="tech-showcase-visual"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <HFTTerminal />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section with Background */}
      <section className="mission-section-enhanced">
        <div className="mission-background">
          <div className="mission-gradient"></div>
        </div>
        <motion.div 
          className="mission-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="display-medium">Our Mission</h2>
          <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.75' }}>
            Our mission is to advance institutional quantitative research and proprietary trading by combining mathematical rigour, artificial intelligence, disciplined risk management, and continuous innovation while creating long-term value through responsible deployment of our own capital.
          </p>
          <div className="mission-cta-group">
            <Link to="/about" className="btn-primary btn-glow">
              Learn More About Us
              <ArrowRight size={20} />
            </Link>
            <Link to="/careers" className="btn-secondary">
              View Open Positions
              <ArrowRight size={20} />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Trust Indicators */}
      <section className="trust-section">
        <div className="trust-content">
          <motion.h3 
            className="heading-1 trust-title"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Trusted & Compliant
          </motion.h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.80)', maxWidth: '700px', margin: '0 auto 28px', textAlign: 'center', fontSize: '15px', lineHeight: '1.6' }}>
            Operating with strict regulatory compliance, statutory adherence, and high corporate governance standards across all market activities.
          </p>
          <div className="trust-badges">
            {TRUST_BADGES.map((badge, index) => (
              <motion.div
                key={index}
                className="trust-badge"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Shield size={20} />
                {badge}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
